import { useContext } from "react";

import AuthContext from "../../contexts/auth/index";
import { sqlDateFormat, translate } from "../../utils/Common";
import { cn } from "@/lib/utils";

/**
 * One locker row.
 *
 * The status used to be a bare coloured dot: online and offline were told apart
 * by hue alone, which anyone with a colour vision deficiency cannot do, and
 * which says nothing at all in a screenshot or a print. It now carries its own
 * word, with the dot as reinforcement.
 *
 * The four counts are right-aligned and tabular: columns of numbers only read
 * as a column when their digits line up.
 */
const num = "px-3 py-2 text-right text-[13px] tabular-nums";

const ItemStatusPlatforms = ({ item, onOpen }) => {
  const userContext = useContext(AuthContext);
  const online = item.Estatus === "ONLINE";
  const id = item.IdPlataforma ?? item.id;
  const canOpen = Boolean(onOpen && id);

  return (
    <tr
      // Navigable, not merely clickable: keyboard users get the same door.
      role={canOpen ? "link" : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onClick={() => canOpen && onOpen(id, item.Plataforma)}
      onKeyDown={(e) => {
        if (canOpen && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onOpen(id, item.Plataforma);
        }
      }}
      className={cn(
        "border-b border-border/60 transition-colors last:border-0",
        canOpen
          ? "cursor-pointer hover:bg-accent/40 focus-visible:bg-accent/60 focus-visible:outline-none"
          : "hover:bg-accent/40"
      )}
    >
      <td className="max-w-[18rem] truncate px-3 py-2 text-[13px] font-medium text-foreground">
        {item.Plataforma}
      </td>

      <td className="px-3 py-2">
        {item.Estatus && (
          <span
            className={cn(
              "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5",
              "text-[10px] font-semibold uppercase tracking-wider",
              // Fill, no outline. 15% is the landing point between a 12% tint
              // that read as no background at all and a 20% one that shouted.
              online
                ? "bg-success/15 text-success"
                : "bg-destructive/15 text-destructive"
            )}
          >
            <span aria-hidden="true" className="size-1.5 rounded-full bg-current opacity-70" />
            {online
              ? translate("dashboard.home.status_platforms.status.online")
              : translate("dashboard.home.status_platforms.status.offline")}
          </span>
        )}
      </td>

      {userContext.user?.IsAdmin && (
        <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
          {!online && item.LastConnection ? sqlDateFormat(item.LastConnection, 1) : "—"}
        </td>
      )}

      <td className={cn(num, "text-foreground")}>{item.CantPaquetes || 0}</td>
      <td className={cn(num, "text-muted-foreground")}>{item.CantPaquetesReservados || 0}</td>
      <td
        className={cn(
          num,
          // Lo que hay que ir a buscar al locker: returns y drop-offs vigentes.
          // Se destaca en tinta primaria cuando hay algo pendiente porque es
          // trabajo por hacer, no un problema como el vencido.
          item.CantPaquetesColectar > 0 ? "font-semibold text-foreground" : "text-muted-foreground"
        )}
      >
        {item.CantPaquetesColectar || 0}
      </td>
      <td
        className={cn(
          num,
          // An expired package is the one number on this row that needs acting on.
          item.CantPaquetesVencidos > 0 ? "font-semibold text-destructive" : "text-muted-foreground"
        )}
      >
        {item.CantPaquetesVencidos || 0}
      </td>
    </tr>
  );
};

export default ItemStatusPlatforms;
